import { REPORT_TYPE } from '../../../constants/report_type';
import {addCompositeKey, extractColumnKey, getCompositeKeys} from './composite_keys';
import {ACCURACY_REPORT_CONFIG, SNAPSHOT_ID} from "../common/constants";
import {getNonAggregatedFieldsWithMultiValues} from "../../../utils/payload";
import {replaceLiveSnapshotId} from "./transformations";

export const reportPreferences = (savedPreferences, columnsJson) => {
  const userPreferences = savedPreferences ? savedPreferences.reportPreferences : [];
  const defaultPreferences = [
    {
      columns: columnsJson.default.columns,
      columnSets: [columnsJson.default.id],
      reportType: REPORT_TYPE.COMPONENT,
    },
    {
      columns: columnsJson.plSets[0].columns,
      columnSets: [columnsJson.plSets[0].id],
      reportType: REPORT_TYPE.PL,
    },
    {
      columns: columnsJson.forecastSets[0].columns,
      columnSets: [columnsJson.forecastSets[0].id],
      reportType: REPORT_TYPE.FORECAST,
    },
  ];

  return { userPreferences, defaultPreferences };
};

export const  getVisibleColumns = (reportPreferences, payloads, liveSnapshot) => {
  const reportType = payloads[0].report.toUpperCase();
  const { userPreferences, defaultPreferences } = reportPreferences;
  let visibleColumns = [];

  if (hasPreferences(userPreferences, reportType)) {
    visibleColumns = getColumnsFromPreferences(userPreferences, reportType);
  } else if (hasPreferences(defaultPreferences, reportType)) {
    visibleColumns = getColumnsFromPreferences(defaultPreferences, reportType);
  }

  visibleColumns = generateVisibleColumns(visibleColumns, payloads, liveSnapshot);

  return visibleColumns;
};

const generateVisibleColumns = (visibleColumns, payloads, liveSnapshot) => {
  const reportType = payloads[0].report.toUpperCase();
  let agg_columns = new Set();

  const aggregatedFields = getNonAggregatedFieldsWithMultiValues(payloads);
  const dataSetKeys = [SNAPSHOT_ID].concat(aggregatedFields || []);
  let compositeKeys = getCompositeKeys(replaceLiveSnapshotId(payloads, liveSnapshot), dataSetKeys);

  for (let k of compositeKeys) {
    for (let c of visibleColumns) {
      agg_columns.add(ACCURACY_REPORT_CONFIG[reportType].DEDUPED_COL_KEYS.includes(c) ? c :
          addCompositeKey(c, k));
    }
  }

  return [...agg_columns];
}

export const findColumnSetItems = (columnsJson, columnSetIds, reportType) => {
  const sets = columnsJson[`${reportType.toLowerCase()}Sets`];
  return sets.filter((set) => (columnSetIds || []).includes(set.id));
};

export const getColumnSets = (columnsJson, columnSetsToLoad, columnSetOverridesToLoad, reportType) => {
  const sets = columnsJson[`${reportType.toLowerCase()}Sets`];

  const selectedColumnSets = [];
  const columnSetOverrides = {};

  if (columnSetsToLoad.length) {
    for (const set of sets) {
      if (columnSetsToLoad.includes(set.id)) {
        selectedColumnSets.push(set);
      }
    }
  }

  if (columnSetOverridesToLoad.length) {
    for (const override of columnSetOverridesToLoad) {
      columnSetOverrides[override] = override;
    }
  }

  return { selectedColumnSets, columnSetOverrides };
};

export const buildContentSelectorOption = (columnKey, columnHeading, columnSets, reportPreferences, reportType) => {
  return {
    id: columnKey,
    label: columnHeading,
    editable: true,
    visible: isColumnVisible(extractColumnKey(columnKey), columnSets, reportPreferences, reportType),
  };
};

export const deriveColumnSets = (columnSets, reportPreferences, reportType) => {
  const { selectedColumnSets } = columnSets;
  if (selectedColumnSets && selectedColumnSets.length) {
    return selectedColumnSets.map((s) => s.id);
  }

  const { userPreferences, defaultPreferences } = reportPreferences;

  const userPrefColumnSets = (Object.keys(userPreferences || {}).length ? userPreferences : []).find(
    (p) => p.reportType.toUpperCase() === reportType
  );

  const defaultPrefColumnSets = (Object.keys(defaultPreferences || {}).length ? defaultPreferences : []).find(
    (p) => p.reportType.toUpperCase() === reportType
  );

  return (userPrefColumnSets || {}).columnSets || (defaultPrefColumnSets || {}).columnSets;
};

const isColumnVisible = (columnKey, columnSets, reportPreferences, reportType) => {
  if (columnSets.columnsForPayloadsThresholdCrossed) {
    return columnSets.columnsForPayloadsThresholdCrossed.columns.includes(columnKey);
  }

  if (columnSets.columnsForForecastAllMarketplaces) {
    return columnSets.columnsForForecastAllMarketplaces.columns.includes(columnKey);
  }

  if (columnSets.allMarketplacePlSets) {
    return columnSets.allMarketplacePlSets[0].columns.includes(columnKey);
  }

  if (hasColumnsSelectedByUser(columnSets)) {
    return specifiedInUserSelection(columnKey, columnSets);
  }

  // If column sets are specfied,  check if column is specified in column set
  if (hasSelectedColumnSets(columnSets)) {
    return specifiedInColumnSet(columnKey, columnSets);
  }

  if (columnSets.columnSetChanged && !hasSelectedColumnSets(columnSets)) {
    return hasColumn(reportPreferences.defaultPreferences, columnKey, reportType);
  }

  // Check if column was specified in report preferences
  return specifiedInReportPreferences(columnKey, reportPreferences, reportType);
};

const hasColumnsSelectedByUser = (columnSets) => {
  const { selectedColumnsByUser } = columnSets || {};
  return selectedColumnsByUser && selectedColumnsByUser.length;
};

const hasSelectedColumnSets = (columnSets) => {
  const { selectedColumnSets } = columnSets || {};
  return selectedColumnSets && selectedColumnSets.length;
};

const specifiedInUserSelection = (columnKey, columnSets) => {
  const { selectedColumnsByUser } = columnSets || {};
  return selectedColumnsByUser.map((c) => extractColumnKey(c)).includes(columnKey);
};

const specifiedInColumnSet = (columnKey, columnSets) => {
  const { selectedColumnSets, columnSetOverrides } = columnSets;

  if (!columnSetOverrides || !Object.keys(columnSetOverrides).length) {
    return specifiedInSelectedColumnSets(columnKey, columnSets);
  }

  for (let columnSet of selectedColumnSets) {
    if (columnSet.columns.includes(columnKey)) {
      return !Object.keys(columnSetOverrides).includes(columnKey);
    }
  }

  return Object.keys(columnSetOverrides).includes(columnKey);
};

const specifiedInSelectedColumnSets = (columnKey, columnSets) => {
  const { selectedColumnSets } = columnSets;
  return selectedColumnSets.some((columnSet) => columnSet.columns.includes(columnKey));
};

const specifiedInReportPreferences = (columnKey, reportPreferences, reportType) => {
  const { userPreferences, defaultPreferences } = reportPreferences;

  if (hasPreferences(userPreferences, reportType)) {
    // User has specified preferences - so check if column is specifed there.
    return hasColumn(userPreferences, columnKey, reportType);
  }

  // If no user preferences - check in default preferences.
  return hasColumn(defaultPreferences, columnKey, reportType);
};

const hasColumn = (preferences, columnKey, reportType) => {
  const prefForReportType = (preferences || []).find((p) => p.reportType.toUpperCase() === reportType);
  return prefForReportType && prefForReportType.columns && prefForReportType.columns.includes(columnKey);
};

const hasPreferences = (preferences, reportType) => {
  const prefForReportType = (preferences || []).find((p) => p.reportType.toUpperCase() === reportType);
  return prefForReportType && prefForReportType.columns && prefForReportType.columns.length;
};

const getColumnsFromPreferences = (preferences, reportType) => {
  const prefForReportType = (preferences || []).find((p) => p.reportType.toUpperCase() === reportType);
  return prefForReportType ? prefForReportType.columns : [];
};



// WEBPACK FOOTER //
// ./src/components/reports/utils/report_preferences.js